import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { requestsApi } from '../services/api';
import Navbar from '../components/Navbar';
import ResponseViewer from '../components/ResponseViewer';

const RequestDetail = () => {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [responses, setResponses] = useState({ data: [], page: 1, pages: 1 });
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);

  const loadRequest = useCallback(async () => {
    try {
      const res = await requestsApi.getOne(id);
      setRequest(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load request');
    }
  }, [id]);

  const loadResponses = useCallback(async (page) => {
    try {
      const res = await requestsApi.getResponses(id, page);
      setResponses(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load responses');
    }
  }, [id]);

  useEffect(() => {
    loadRequest();
    loadResponses(1);
  }, [loadRequest, loadResponses]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await requestsApi.runNow(id);
      await loadRequest();
    } catch (err) {
      setError(err.response?.data?.error || 'Run now failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/research" className="text-sm text-primary-600 hover:text-primary-700 mb-4 inline-block">
          &larr; Back to Research
        </Link>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {!request && !error && (
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {request && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">{request.name}</h2>
            <div className="flex gap-2 mb-3">
              <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                {request.frequency === 'weekdays' ? 'Weekdays' : request.frequency}
              </span>
              {request.time && (
                <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  at {request.frequency === 'weekly' && request.day_of_week != null
                    ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][request.day_of_week] + ' '
                    : ''}{request.time} UTC
                </span>
              )}
              {Boolean(request.email_notify) && (
                <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  email
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 whitespace-pre-wrap mb-3">{request.prompt}</p>
            <div className="flex items-center gap-6 text-xs text-gray-400">
              <span>
                Last run: {request.last_run_time ? new Date(request.last_run_time).toLocaleString() : 'Never'}
              </span>
              <span>
                Next run: {request.next_run_time ? new Date(request.next_run_time).toLocaleString() : '—'}
              </span>
              <button
                onClick={handleRunNow}
                disabled={running}
                className="text-sm px-3 py-1 border border-green-300 text-green-600 rounded-md hover:bg-green-500 hover:text-white transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {running ? (
                  <>
                    <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Running...
                  </>
                ) : (
                  'Run Now'
                )}
              </button>
            </div>
          </div>
        )}

        <h3 className="text-lg font-semibold text-gray-900 mb-3">Response History</h3>
        <ResponseViewer
          responses={responses}
          onPageChange={(p) => loadResponses(p)}
        />
      </div>
    </div>
  );
};

export default RequestDetail;
